import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Textarea, Label } from '@/components/ui'
import { Search } from 'lucide-react'

interface StringInfo {
  length: number
  words: number
  lines: number
  chars: { [key: string]: number }
}

export function StringInspector() {
  const [input, setInput] = useState('')
  const [info, setInfo] = useState<StringInfo | null>(null)

  const inspectString = (text: string) => {
    const lines = text.split('\n')
    const words = text.split(/\s+/).filter(w => w.length > 0)
    const chars: { [key: string]: number } = {}
    for (const char of text) {
      chars[char] = (chars[char] || 0) + 1
    }
    setInfo({
      length: text.length,
      words: words.length,
      lines: lines.length,
      chars,
    })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
            <Search className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">String Inspector</h1>
            <p className="text-muted-foreground">Get detailed information on strings and texts</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Enter the text to inspect</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                inspectString(e.target.value)
              }}
              className="h-96 font-mono"
            />
          </CardContent>
        </Card>
        <Card className="glass">
          <CardHeader>
            <CardTitle>Information</CardTitle>
          </CardHeader>
          <CardContent>
            {info && (
              <div className="space-y-4">
                <p>Length: {info.length}</p>
                <p>Words: {info.words}</p>
                <p>Lines: {info.lines}</p>
                <h3 className="text-lg font-semibold">Character Count:</h3>
                <pre className="font-mono p-4 bg-muted/30 rounded-lg overflow-auto max-h-60">
                  {Object.entries(info.chars).map(([char, count]) => (
                    <div key={char}>
                      '{char}': {count}
                    </div>
                  ))}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
